// Plan confirmation for one opportunity: POSTs the reviewed plan out of the
// planning modal and the backend (GrantSuite::ConfirmPlan, ENG-1823) writes
// it into real milestones, tasks, required documents, and requirements
// atomically. The payload is the human-edited review copy, never the raw
// proposal artifact — the payload shape mirrors the controller's permit
// list in GrantOpportunityPlanConfirmationsController.

export type PlanConfirmationTask = {
  title: string;
  description?: string | null;
  due_at?: string | null;
  source_passage?: string | null;
};

export type PlanConfirmationMilestone = {
  title: string;
  description?: string | null;
  due_at?: string | null;
  // A collaborator id from the review editor's owner selector (ENG-1981);
  // ConfirmPlan writes it onto the created milestone's owner.
  owner_id?: number | null;
  source_passage?: string | null;
  tasks: PlanConfirmationTask[];
};

export type PlanConfirmationRequiredDocument = {
  name: string;
  description?: string | null;
  source_passage?: string | null;
};

export type PlanConfirmationRequirement = {
  description: string;
  category?: string | null;
  source_passage?: string | null;
};

export type PlanConfirmationPayload = {
  // The reviewed run this plan came from. The server stamps its
  // confirmed_at under a row lock and 409s a second confirm of the same
  // run, so a reload or second browser cannot double-create the records.
  plan_proposal_id?: number | null;
  proposed_title?: string | null;
  milestones: PlanConfirmationMilestone[];
  required_documents: PlanConfirmationRequiredDocument[];
  requirements: PlanConfirmationRequirement[];
};

export type PlanConfirmationResult = {
  milestone_ids: number[];
  task_count: number;
  required_document_count: number;
  requirement_count: number;
  title: string;
};

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

export async function confirmPlan(
  municipalityId: number,
  opportunityId: number,
  plan: PlanConfirmationPayload,
): Promise<PlanConfirmationResult> {
  const response = await fetch(
    `/api/v1/municipalities/${municipalityId}/grant_opportunities/${opportunityId}/plan_confirmation`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
      },
      body: JSON.stringify({ plan_confirmation: plan }),
    },
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.errors?.[0] ?? body.error ?? "Failed to confirm the plan");
  }
  return response.json();
}
